import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { OneAlert, OneButton, OneMessage, OneModal } from '@yy/one-ui'
import { Toast } from 'antd-mobile'
import { Form } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import useMmcToken from '@common/oneclub/hooks/useMmcToken'
import { CloseCircleFilled, CloseOutlined } from '@ant-design/icons'
import Cookies from 'js-cookie'
import { Dialog } from 'antd-mobile'
import Udb, { LoginVerifyParams } from '@yy/oneship-udb'
import RULES from '@common/oneclub/constants/rules'
import { useDispatch, useSelector } from '@common/oneclub/store'
import { useRedirect } from '@common/oneclub/hooks'
import { useOneclubConfigs } from '@common/oneclub/configuration'
import { setLogin, setCanSubmit, getRegisterConfig, getBaseUserInfo } from '@common/oneclub/store/authSlice'
import { useValidateError, useCaptchaLanguageChange } from '@common/oneclub/hooks'
import { UdbResCodeEnum, UDB_CONF, SOURCE_SYSTEM } from '@common/oneclub/constants/'
import { getCountryByIp } from '@common/oneclub/services/api'
import { useIsPc } from '@common/oneclub/components/Device'
import { getOneClubPath, getQuery } from '@common/oneclub/utils/'
import { dataLayerPush } from '@common/oneclub/utils/dataLayer'
import { AccountType, AccountTypeChoose } from '../AccountTypeChose'
import { Captcha, PasswordInput, MobileEmailInput } from '@common/oneclub/components'
import { LoginPublicKey } from '@common/oneclub/constants/publicKey'
import { TwoStepVerify } from '@sl/merchant-security-npm'

import styles from './index.module.less'
import { useBoolean, useMemoizedFn, useMount } from 'ahooks'
import { TwoStepVerifySetting } from '../TwoStepVerifySetting'
import { check2fa } from '@common/oneclub/services/api/bff/info/api'
import { UserTwoFactorAuthRespDTO } from '@common/oneclub/services/api/bff/info/type'
import { usePromise } from '@common/oneclub/hooks/utilsHooks'
import { udbSentryReport } from '@common/oneclub/services/http'

const signinInstance = Udb.signin(UDB_CONF)
const pwdSafeUdb = Udb.pwdSafe(UDB_CONF)

interface LoginBoxProps {
  loginSuccessCb: (ticket: string) => void
  hasReturnUrl?: boolean
  dfptoken: string
}
const accLableMap = {
  email: 'udb.label.email',
  mobile: 'common.mobile',
}

export const LoginBox: React.FC<LoginBoxProps> = ({ loginSuccessCb, hasReturnUrl, dfptoken }) => {
  const isPc = useIsPc()
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { historyPush } = useRedirect()
  const query = getQuery()
  const { country_list } = useOneclubConfigs()
  const languageStore = useSelector((state) => state?.language)

  const captchaTokenRef = useRef('')
  const identityTokenRef = useRef('')
  const stokenRef = useRef('')
  const canSubmit = useSelector((state) => state?.auth.canSubmit)
  const mtokenRef = useRef('')

  const [accountType, setAccountType] = useState<AccountType>('email')
  const [loading, setLoading] = useState(false)
  const [captchaVisible, setCaptchaVisible] = useState(false)
  const [captchaType, setCaptchaType] = useState<string | undefined>(undefined)
  const [bizParam, setBizParam] = useState<Record<string, any> | undefined>(undefined)
  const [inputType, setInputType] = useState('')
  const [rickTipVisible, setRickTipVisible] = useState(false)
  const captchaLang = useCaptchaLanguageChange()
  const publicKey = LoginPublicKey[query.app]
  // 使用hooks
  const { getMToken } = useMmcToken({ id: 'loginSubmit', publicKey })
  const [showTwoStepVerify, setTwoStepVerify] = useBoolean()
  const { resolve: on2faFinish, reject: on2faCancel, newPromise } = usePromise<string>()
  const [twoFARes, setTwoFARes] = useState<UserTwoFactorAuthRespDTO & { res: () => void }>(null)

  const get2faToken = useMemoizedFn(async (error: any) => {
    setCaptchaType('authn')
    setBizParam({ serialNo: error.extras?.response?.data.serialNo || '' })
    setTwoStepVerify.setTrue()
    try {
      const token = await newPromise()
      setTwoStepVerify.setFalse()
      return token
    } catch (error) {}
    setTwoStepVerify.setFalse()
  })

  useValidateError(form)
  /**
   * 忘记密码
   */
  const toForgotHanlder = useCallback(() => {
    historyPush('/user/forgot')
  }, [historyPush])

  /** 登录后检查，密码有效期，2fa等 */
  const checkLoginSuccessCb = useCallback(
    async (ticket: string) => {
      try {
        const uid = Cookies.get('osudb_uid')
        const [res, twoFaRes] = await Promise.all([pwdSafeUdb.queryPopup({ uid, ticketType: '1' }), check2fa()])
        // 先检查密码是否已接近有效期，再登录
        const day = Math.ceil(res.data.pwdRemainingTime / (24 * 3600 * 1000))
        if ([30, 15, 7, 1].includes(day)) {
          await new Promise<void>((resolve, reject) => {
            const confirmProps = {
              title: t('passwordExpired.days.title', { day }),
              content: t('passwordExpired.days.desc'),
              onCancel: () => {
                resolve()
              },
              onOk: async () => {
                reject()
                const userInfo = await dispatch(getBaseUserInfo())
                const url = getOneClubPath('/user/changePassword', languageStore.lang, {
                  email: userInfo.udbUserInfo?.email,
                  phone: userInfo.udbUserInfo?.phone,
                  source: userInfo.registrySource || 'email',
                  uid,
                })
                historyPush(url)
              },
              okText: t('update.password.now'),
              cancelText: t('update.password.later'),
            }
            if (isPc) OneModal.confirm(confirmProps)
            else {
              Dialog.confirm({
                ...confirmProps,
                onConfirm: confirmProps.onOk,
                confirmText: confirmProps.okText,
              })
            }
            pwdSafeUdb.updatePopup({ uid, ticketType: '1', day: day.toString() })
          })
        }

        // 是否显示2fa提示
        if (!twoFaRes.enabled) {
          await new Promise<void>((res) => {
            setTwoFARes({ ...twoFaRes, res })
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
      loginSuccessCb(ticket)
    },
    [dispatch, historyPush, isPc, languageStore.lang, loginSuccessCb, t]
  )

  /**
   * 初始化
   */
  useEffect(() => {
    dfptoken &&
      signinInstance.init({
        type: 'acct',
        dfptoken,
      })
  }, [dfptoken])

  /**
   * 自动选择区号
   */
  useEffect(() => {
    getCountryByIp().then(({ data }) => {
      const item = country_list?.find((item) => item.value === data.countryCode)
      if (item) {
        form.setFieldsValue({
          areaCode: item.areaCode.substring(1),
        })
      }
    })
  }, [form, country_list])

  /**
   * 设置按钮可否提交
   */
  const onFieldsChange = (changedFields, allFields) => {
    const isValid = allFields.every(({ errors, value }) => !errors.length && !!value)
    dispatch(setCanSubmit(isValid))
    if (changedFields.find((item) => item.name[0] === 'account')) {
      // 账号框输入中，文案要提示为请输入邮箱或手机
      if (inputType) {
        setInputType('')
      }
    }
  }

  /** 失焦判断输入是邮箱还是手机号 */
  const onBlurValidateAccType = useCallback(async () => {
    const { account } = await form.validateFields(['account'])

    try {
      if (await RULES.email().validator('', account)) {
        setInputType('email')
        return { inputType: 'email' }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    }
    try {
      if (await RULES.mobile().validator('', account)) {
        setInputType('mobile')
        return { inputType: 'mobile' }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    }
  }, [form])

  const [passwordExpiredText, setPasswordExpiredText] = useState('')
  const showRickTip = useCallback(() => {
    if (!isPc) {
      Toast.show({
        content: t('risk.tip.detail'),
        duration: 5000,
      })
    } else {
      setRickTipVisible(true)
    }
  }, [isPc, t])

  // udb特殊错误处理
  const onErrorMap = useMemo(
    () => ({
      [UdbResCodeEnum.PASSWORD_EXPIRED_CODE]: () => {
        setPasswordExpiredText(t('udb.rescode.1042.loginType', { loginType: accountType }))
      },
      [UdbResCodeEnum.TWO_STEP_VERIFY]: get2faToken,
    }),
    [accountType, get2faToken, t]
  )

  // 风控：校验成功
  const onCaptchaSuccess = useMemoizedFn((token: string, captchaType?: string) => {
    if (captchaType === 'authn') {
      identityTokenRef.current = token
    } else {
      captchaTokenRef.current = token
    }
    const values = form.getFieldsValue(true)
    setCaptchaVisible(false)
    onSubmit(values, false)
  })

  /**
   * 表单提交
   * needInit 出现风控，不需要调用init接口获取stoken
   */
  const onSubmit = useCallback(
    async (values, needInit = true) => {
      const { inputType } = await onBlurValidateAccType()
      const { areaCode, pwd, account } = values
      setLoading(true)
      try {
        const acct = inputType === 'email' ? account : '00' + areaCode + account
        const params: LoginVerifyParams & {
          /** 身份验证token */
          identityToken?: string
          /** 风控序列号，上一个请求返回 */
          serialNo?: string
          /** 风控人机token */
          mtoken?: string
        } = {
          acct,
          pwd,
          cas: 1,
          mtoken: mtokenRef.current,
          extinfo: JSON.stringify({ sourceSystem: SOURCE_SYSTEM[query.app] }),
        }
        if (!needInit) {
          if (!captchaType) {
            params.captcha = captchaTokenRef.current
            params.stoken = stokenRef.current
            params.cas = 1
          }
          if (captchaType === 'authn') {
            params.identityToken = identityTokenRef.current
            params.serialNo = bizParam?.serialNo
          }
        }
        const res = needInit ? await signinInstance.verify(params) : await signinInstance.verifyWithNoInit(params)
        dispatch(
          setLogin({
            isLogined: true,
            osudb_castgc: Cookies.get('osudb_castgc'),
          })
        )
        checkLoginSuccessCb(res?.data?.casTicket)
        dataLayerPush({ pageCategory: 'Login', event: 'login', action: 'click', loginStatus: 'success' })
      } catch (error) {
        if ([UdbResCodeEnum.LOGIN_DYNAMIC_CAPTCHA_CODE].includes(error.code)) {
          // 显示风控滑块
          stokenRef.current = error.extras.response.stoken
          setCaptchaType(undefined)
          setBizParam(undefined)
          setCaptchaVisible(true)
        } else if (error.code === UdbResCodeEnum.LOGIN_EMAIL_CAPTCHA_ERROR_CODE) {
          // 邮箱验证码错误，需要特殊的验证码类型
          stokenRef.current = error.extras.response.stoken
          setCaptchaType('authn')
          setBizParam({ serialNo: error.extras?.response?.data.serialNo || '' })
          setCaptchaVisible(true)
        } else if (error.code === UdbResCodeEnum.LOGIN_RISK_ACCOUNT_CODE) {
          showRickTip()
        } else if (error.code === UdbResCodeEnum.TWO_STEP_VERIFY) {
          // 2fa验证
          const token = await get2faToken(error)
          if (token) onCaptchaSuccess(token, 'authn')
        } else if (error.code === UdbResCodeEnum.PASSWORD_EXPIRED_CODE) {
          setPasswordExpiredText(t('udb.rescode.1042'))
        } else {
          OneMessage.error(t(`udb.rescode.${error.code}`))
          udbSentryReport('登录异常', error)
        }
        dataLayerPush({ pageCategory: 'Login', event: 'login', action: 'click', loginStatus: 'failure' })
      } finally {
        setLoading(false)
        captchaTokenRef.current = ''
      }
    },
    [onBlurValidateAccType, query.app, dispatch, checkLoginSuccessCb, captchaType, bizParam?.serialNo, showRickTip, get2faToken, onCaptchaSuccess, t]
  )

  const handleOnSumbit = async () => {
    const values = form.getFieldsValue()
    const token = await getMToken()
    mtokenRef.current = token
    onSubmit(values)
  }

  const loginTypeChange = useCallback(
    (item: AccountType) => {
      form.resetFields(['pwd'])
      setAccountType(item)
    },
    [form]
  )

  // 风控：取消验证
  const onCaptchaCancel = useCallback(() => {
    setCaptchaVisible(false)
  }, [])

  const toRegister = useCallback(() => {
    historyPush('/user/register')
  }, [historyPush])

  useMount(() => {
    dispatch(getRegisterConfig())
  })

  return (
    <div className={classNames(styles.loginBox)}>
      {twoFARes && !twoFARes.enabled ? (
        <TwoStepVerifySetting loginSuccessCb={twoFARes.res} mustSet={twoFARes.twoFactorAuthPolicy === 1} />
      ) : (
        <>
          <div style={{ display: showTwoStepVerify ? 'none' : 'block' }}>
            {/* 只能使用这种方式隐藏，因为useMmcToken里面有注册监听器 */}
            <div className={classNames(styles.title, { [styles.titleh5]: !isPc })}>{t('login.title')}</div>
            {/* 风控提示 */}
            {rickTipVisible && (
              <div className={styles.rickTip}>
                <div className={styles.rickTipIcon}>
                  <CloseCircleFilled />
                </div>
                <div className={styles.rickTipContentWrapper}>
                  <div className={styles.rickTipTitle}>{t('risk.tip')}</div>
                  <div className={styles.rickTipContent}>{t('risk.tip.detail')}</div>
                </div>
                <div className={styles.rickTipClose} onClick={() => setRickTipVisible(false)}>
                  <CloseOutlined />
                </div>
              </div>
            )}
            {passwordExpiredText ? (
              <OneAlert
                message={
                  <div style={{ fontSize: '14px' }}>
                    <span>{passwordExpiredText}</span>
                    <a onClick={toForgotHanlder} style={{ color: '#356DFF', marginLeft: '8px' }}>
                      {t('update.password.now')}
                    </a>
                  </div>
                }
                type="error"
                style={{ marginBottom: '20px' }}
              />
            ) : null}

            <Form form={form} onFieldsChange={onFieldsChange} className={styles.loginForm}>
              <Form.Item name="account" rules={[RULES.account()]}>
                <MobileEmailInput
                  placeholder={inputType ? t(accLableMap[inputType]) : t('login.placeholder')}
                  onBlurValidateAccType={onBlurValidateAccType}
                  inputType={inputType}
                  watchAutoFill
                  onChangeAreaCode={(val) => form.setFieldsValue({ areaCode: val })}
                  onCountryChange={(val) => form.setFieldsValue({ mobileCountryCode: val })}
                />
              </Form.Item>
              <Form.Item name="pwd" rules={[RULES.required()]} style={{ marginBottom: 16 }}>
                <PasswordInput autoComplete="current-password" placeholder={t('udb.label.pwd')} watchAutoFill />
              </Form.Item>
              <Form.Item name="areaCode" hidden noStyle initialValue={country_list?.[0]?.areaCode?.substring(1)} rules={[{ required: true }]} />
              <Form.Item name="countryCode" hidden noStyle initialValue={country_list?.[0]?.label} rules={[{ required: true }]} />
              <Form.Item name="mobileCountryCode" hidden noStyle initialValue={country_list?.[0]?.label} />
              <div className={styles.forgotPwd}>
                <span onClick={toForgotHanlder}>{t('login.forger')}</span>
              </div>
              <Form.Item className={styles.btnFormItem}>
                <OneButton id="loginSubmit" type="primary" loading={loading} disabled={!canSubmit} color="primary" className={styles.normalButton} onClick={handleOnSumbit}>
                  {t('login.title')}
                </OneButton>
              </Form.Item>
            </Form>

            <AccountTypeChoose
              pageType="login"
              accountType={accountType}
              changeAccountType={loginTypeChange}
              loginSuccessCb={checkLoginSuccessCb}
              hasReturnUrl={hasReturnUrl}
              showRickTip={showRickTip}
              onErrorMap={onErrorMap}
            />

            <div className={styles.registerTips}>
              {t('login.hasNoAccount')}？
              <div className={styles.registerButton} onClick={toRegister}>
                {t('login.signUpTitle')}
              </div>
            </div>
          </div>
          {showTwoStepVerify ? (
            <TwoStepVerify
              env={process.env.SECURITY_ENV}
              bizSource={process.env.SECURITY_BIZ_SOURCE}
              appCode="amljpt"
              serialNo={bizParam.serialNo}
              onFinish={on2faFinish}
              onCancel={on2faCancel}
              lang={languageStore.languageType}
            />
          ) : null}
        </>
      )}

      {/* 验证滑块弹窗 */}
      {captchaVisible && (
        <Captcha
          id="captcha-login-box"
          visible={captchaVisible}
          className={styles.captcha}
          lang={captchaLang}
          captchaType={captchaType}
          bizParam={bizParam}
          onSuccess={onCaptchaSuccess}
          onCancel={onCaptchaCancel}
        />
      )}
    </div>
  )
}

LoginBox.displayName = 'LoginBox'
export default LoginBox
